import { useState } from 'react';
import { AlertTriangle, Check, Copy, ShieldCheck } from 'lucide-react';
import type { ChunkSample } from '../../types/throttle';
import {
  NormalizedChunkSample,
  TICK_BUDGET_MS,
  buildTeleportCommand,
  chunkCenterBlock,
  chunkMsptThresholdClass,
  formatDimension,
  getMsptSeverity,
  normalizeChunkSample,
} from '../../utils/throttle';
import { cn } from '../../utils/cn';

interface FlaggedChunksTableProps {
  /** `problematicChunks` of the live flagged feed (already sorted worst-first). */
  chunks: ChunkSample[];
  /**
   * Lowest MSPT at which this server's effective rules flag a regular chunk
   * (see `getFlaggingFloorMspt`); `null` = built-in defaults / unknown.
   */
  flaggingThreshold: number | null;
  /** Current server MSPT — drives the empty-state copy (empty list ≠ all clear). */
  mspt: number | null | undefined;
}

const chunkKey = (chunk: NormalizedChunkSample) => `${chunk.dimension}:${chunk.posX}:${chunk.posZ}`;

/**
 * Flagged-chunk table for the server detail page. Flagged chunks are in
 * WARNING or THROTTLED state — the two are not distinguishable in this API,
 * so every entry is presented as needing staff attention.
 */
export const FlaggedChunksTable = ({ chunks, flaggingThreshold, mspt }: FlaggedChunksTableProps) => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const handleCopyTeleport = (chunk: NormalizedChunkSample) => {
    const key = chunkKey(chunk);
    navigator.clipboard
      .writeText(buildTeleportCommand(chunk.dimension, chunk.posX, chunk.posZ))
      .then(() => {
        setCopiedKey(key);
        setTimeout(() => setCopiedKey((prev) => (prev === key ? null : prev)), 2000);
      });
  };

  if (chunks.length === 0) {
    const lagging = mspt != null && getMsptSeverity(mspt) === 'critical';
    if (lagging) {
      return (
        <div className="alert alert-warning">
          <AlertTriangle />
          <div>
            <p className="text-sm font-semibold">
              Server is lagging but no chunks are flagged
            </p>
            <p className="text-xs text-glass-400 mt-1">
              MSPT is {mspt.toFixed(1)} ms (budget {TICK_BUDGET_MS} ms), yet the throttle sampler
              found nothing — the lag is not attributable to chunk-ticking blocks/entities.
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-start gap-3 p-4 subcard">
        <span className="icon-tile icon-tile-success"><ShieldCheck className="w-4 h-4" /></span>
        <div>
          <p className="text-sm font-semibold text-white">No problematic chunks</p>
          <p className="text-xs text-glass-400 mt-1">
            An empty feed is the normal, healthy state — the anti-lag system has nothing flagged
            on this server right now.
          </p>
        </div>
      </div>
    );
  }

  const threshold = flaggingThreshold;

  // The feed arrives sorted worst-first; re-sorting is a cheap safety net.
  const sorted = chunks.map(normalizeChunkSample).sort((a, b) => b.mspt - a.mspt);

  return (
    <div>
      {/* Desktop table */}
      <div className="hidden md:block subcard overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th className="th">Dimension</th>
              <th className="th" title="Chunk coordinates (multiply by 16 for blocks)">
                Chunk
              </th>
              <th className="th" title="Block coordinates of the chunk center">
                Block center
              </th>
              <th className="th" title="Average ms/tick this chunk cost over the last ~60 s">
                Chunk MSPT
              </th>
              <th className="th">State</th>
              <th className="th text-right">Teleport</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((chunk) => {
              const key = chunkKey(chunk);
              const center = chunkCenterBlock(chunk.posX, chunk.posZ);
              return (
                <tr key={key}>
                  <td className="td text-glass-300" title={chunk.dimension}>
                    {formatDimension(chunk.dimension)}
                  </td>
                  <td className="td text-white font-medium font-mono text-xs tabular-nums">
                    {chunk.posX}, {chunk.posZ}
                  </td>
                  <td className="td text-glass-300 font-mono text-xs tabular-nums">
                    {center.x}, {center.z}
                  </td>
                  <td className="td">
                    <span className={cn('font-medium tabular-nums', chunkMsptThresholdClass(chunk.mspt, threshold))}>
                      {chunk.mspt.toFixed(2)} ms
                    </span>
                    {threshold != null && (
                      <span className="ml-2 text-xs text-glass-500 tabular-nums">
                        {(chunk.mspt / threshold).toFixed(1)}× threshold
                      </span>
                    )}
                  </td>
                  <td className="td">
                    <span
                      className="badge badge-amber cursor-default"
                      title="WARNING and THROTTLED states are not distinguishable in this feed"
                    >
                      Needs staff attention
                    </span>
                  </td>
                  <td className="td text-right">
                    <button
                      onClick={() => handleCopyTeleport(chunk)}
                      className="glass-button-sm"
                      title={buildTeleportCommand(chunk.dimension, chunk.posX, chunk.posZ)}
                    >
                      {copiedKey === key ? (
                        <Check className="w-3.5 h-3.5 text-green-400" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                      {copiedKey === key ? 'Copied' : 'Copy /tp'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {sorted.map((chunk) => {
          const key = chunkKey(chunk);
          const center = chunkCenterBlock(chunk.posX, chunk.posZ);
          return (
            <div key={key} className="p-3 subcard">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-white font-mono tabular-nums">
                  {formatDimension(chunk.dimension)} {chunk.posX}, {chunk.posZ}
                </span>
                <span className={cn('text-sm font-medium tabular-nums', chunkMsptThresholdClass(chunk.mspt, threshold))}>
                  {chunk.mspt.toFixed(2)} ms
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-glass-400">
                <span className="font-mono tabular-nums">
                  Block {center.x}, {center.z}
                </span>
                <button
                  onClick={() => handleCopyTeleport(chunk)}
                  className="glass-button-sm touch-manipulation"
                >
                  {copiedKey === key ? (
                    <Check className="w-3.5 h-3.5 text-green-400" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                  {copiedKey === key ? 'Copied' : 'Copy /tp'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-3 text-xs text-glass-500">
        The feed is capped at the 10 worst chunks per dimension — the real number of flagged chunks
        may be higher. Chunk MSPT is averaged over the last ~60 s sampling window.
      </p>
    </div>
  );
};
